<template>
  <div
    class="group relative overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-purple-300 hover:shadow-2xl dark:border-gray-700 dark:bg-gray-800 dark:hover:border-purple-500"
    @mouseenter="isHovering = true"
    @mouseleave="isHovering = false"
  >
    <div
      v-if="showAdminActions"
      class="absolute left-3 top-3 z-30 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
    >
      <label class="flex cursor-pointer items-center">
        <input
          type="checkbox"
          :value="wallpaper.id"
          :checked="isSelected"
          @change="emit('toggle-selection', wallpaper.id)"
          class="h-5 w-5 rounded border-gray-300 bg-white text-purple-600 shadow-lg transition-all duration-200 hover:scale-110 focus:ring-purple-500 dark:border-gray-500 dark:bg-gray-700"
        />
      </label>
    </div>

    <div class="absolute right-3 top-3 z-20 flex flex-col items-end gap-2">
      <div
        v-if="isLiveWallpaper"
        class="flex items-center space-x-1 rounded-full bg-gradient-to-r from-red-500 to-pink-500 px-3 py-1 text-xs font-medium text-white shadow-lg backdrop-blur-sm"
      >
        <span class="relative mr-1 inline-flex h-2 w-2">
          <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75"></span>
          <span class="relative inline-flex h-2 w-2 rounded-full bg-white"></span>
        </span>
        <span>LIVE</span>
      </div>

      <div
        v-if="wallpaper.isWallpaperPack"
        class="flex items-center space-x-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 px-3 py-1 text-xs font-medium text-white shadow-lg backdrop-blur-sm"
      >
        <span class="font-medium">PACK</span>
        <span class="rounded-full bg-white/25 px-2 py-0.5 text-[11px] font-bold">{{ getDeviceCount() }}</span>
      </div>

      <div
        v-else
        class="flex items-center space-x-1 rounded-full px-3 py-1 text-xs font-medium text-white shadow-lg backdrop-blur-sm"
        :class="badgeClass"
      >
        <span>{{ deviceEmoji }}</span>
        <span>{{ deviceShortLabel }}</span>
      </div>
    </div>

    <div
      :class="[
        'relative overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800',
        aspectRatioClass
      ]"
    >
      <SimpleSecureImage
        :image-url="staticPreviewUrl"
        :wallpaper-id="wallpaper.id"
        :is-preview="true"
        :alt="wallpaper.title || `${deviceLabel} wallpaper`"
        :container-class="'absolute inset-0 h-full w-full'"
        :image-class="'h-full w-full object-cover transition-opacity duration-300'"
        :target-aspect="targetAspect"
        :target-width="targetWidth"
        :target-height="targetHeight"
        crop-mode="fill"
      />

      <div
        v-if="isLiveWallpaper && livePreviewUrl && isHovering"
        class="absolute inset-0 z-10 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
      >
        <video
          :src="livePreviewUrl"
          class="h-full w-full object-cover"
          autoplay
          muted
          loop
          playsinline
          preload="metadata"
          @loadeddata="onLivePreviewLoad"
          @error="onLivePreviewError"
        />
      </div>

      <div class="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent opacity-0 transition-all duration-300 group-hover:opacity-100"></div>

      <div class="absolute inset-0 z-20 flex items-center justify-center opacity-0 transition-all duration-300 group-hover:opacity-100">
        <div class="flex translate-y-4 space-x-3 transition-transform duration-300 group-hover:translate-y-0">
          <button
            @click.stop="emit('view', wallpaper)"
            class="rounded-full bg-white/95 p-3 shadow-xl backdrop-blur-sm transition-all duration-200 hover:scale-110 hover:bg-white dark:bg-gray-800/95 dark:hover:bg-gray-700"
            title="View wallpaper"
          >
            <svg class="h-5 w-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
            </svg>
          </button>

          <button
            @click.stop="emit('favorite', wallpaper)"
            class="rounded-full bg-white/95 p-3 shadow-xl backdrop-blur-sm transition-all duration-200 hover:scale-110 hover:bg-white dark:bg-gray-800/95 dark:hover:bg-gray-700"
            title="Add to favorites"
          >
            <svg class="h-5 w-5 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
            </svg>
          </button>

          <button
            v-if="showAdminActions"
            @click.stop="emit('edit', wallpaper)"
            class="rounded-full bg-white/95 p-3 shadow-xl backdrop-blur-sm transition-all duration-200 hover:scale-110 hover:bg-white dark:bg-gray-800/95 dark:hover:bg-gray-700"
            title="Edit wallpaper"
          >
            <svg class="h-5 w-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
            </svg>
          </button>

          <button
            v-if="showAdminActions"
            @click.stop="emit('delete', wallpaper)"
            class="rounded-full bg-white/95 p-3 shadow-xl backdrop-blur-sm transition-all duration-200 hover:scale-110 hover:bg-white dark:bg-gray-800/95 dark:hover:bg-gray-700"
            title="Delete wallpaper"
          >
            <svg class="h-5 w-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
          </button>
        </div>
      </div>

      <div class="absolute bottom-3 right-3 rounded-full bg-black/70 px-2.5 py-1 text-xs text-white backdrop-blur-sm shadow-lg flex items-center space-x-1">
        <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
        </svg>
        <span class="font-medium">{{ wallpaper.downloadCount || 0 }}</span>
      </div>
    </div>

    <div class="p-3">
      <div class="mb-2 flex items-start justify-between gap-3">
        <div class="min-w-0 flex-1">
          <h3 class="truncate text-sm font-semibold text-gray-900 dark:text-white">
            {{ wallpaper.title || `${deviceLabel} wallpaper` }}
          </h3>
          <div class="mt-1 flex items-center justify-between gap-2">
            <span class="truncate rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800 dark:bg-purple-900/30 dark:text-purple-200">
              {{ getCategoryName(wallpaper.category) }}
            </span>
            <span class="shrink-0 text-xs text-gray-500 dark:text-gray-400">
              {{ formatDate(wallpaper.createdAt) }}
            </span>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-1.5 text-xs">
        <div class="flex items-center group/ratio relative text-gray-600 dark:text-gray-400">
          <svg class="mr-1 h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
          </svg>
          <span>{{ aspectRatioLabel }}</span>
        </div>

        <div class="flex items-center justify-end gap-1.5">
          <span class="rounded-full bg-white p-1 border border-gray-200 shadow-sm dark:border-gray-600 dark:bg-gray-700">{{ deviceEmoji }}</span>
          <span class="text-gray-600 dark:text-gray-400">{{ deviceLabel }}</span>
        </div>
      </div>

      <div
        v-if="subtitle?.trim()"
        class="mt-2 rounded-xl bg-gray-50 p-2 text-xs leading-4 text-gray-600 dark:bg-gray-900/60 dark:text-gray-300"
      >
        {{ subtitle }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import SimpleSecureImage from '../ui/SimpleSecureImage.vue'
import type { Wallpaper } from '../../firebase/schema'
import { cloudinaryService } from '../../services/cloudinaryService'
import { isLikelyVideoUrl } from '../../utils/validation'
import type { DeviceWallpaperTab } from '../../composables/useDeviceWallpaperTabs'

interface Props {
  wallpaper: Wallpaper
  categoryMap: Record<string, string>
  showAdminActions?: boolean
  isSelected?: boolean
  device: DeviceWallpaperTab
  deviceLabel: string
  deviceShortLabel: string
  deviceEmoji: string
  subtitle: string
  aspectRatioClass: string
  aspectRatioLabel: string
  targetAspect: number
  targetWidth: number
  targetHeight: number
  badgeClass: string
}

const props = withDefaults(defineProps<Props>(), {
  showAdminActions: false,
  isSelected: false
})

const emit = defineEmits<{
  (e: 'toggle-selection', id: string): void
  (e: 'view', wallpaper: Wallpaper): void
  (e: 'download', wallpaper: Wallpaper): void
  (e: 'favorite', wallpaper: Wallpaper): void
  (e: 'edit', wallpaper: Wallpaper): void
  (e: 'delete', wallpaper: Wallpaper): void
}>()

const wallpaper = computed(() => props.wallpaper)
const isHovering = ref(false)

const getCategoryName = (categoryId?: string) => {
  if (!categoryId) return 'Uncategorized'
  return props.categoryMap[categoryId] || categoryId
}

const formatDate = (date: Date | string) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const getDeviceCount = () => {
  const wp = wallpaper.value
  if (wp.isWallpaperPack && wp.deviceVariations) {
    return Object.keys(wp.deviceVariations).length
  }
  return wp.supportedDevices?.length || (wp.primaryDevice ? 1 : 0)
}

const isLiveWallpaper = computed(() => {
  const wp = wallpaper.value
  const packLiveUrl = getSelectedPackLiveUrl()
  return Boolean(
    wp.isVideo ||
    wp.isAnimated ||
    (wp.videoUrl && wp.videoUrl.trim().length > 0) ||
    isLikelyVideoUrl(wp.imageUrl) ||
    Boolean(packLiveUrl)
  )
})

const getSelectedPackVariation = () => {
  const wp = wallpaper.value

  if (!wp.isWallpaperPack) return null

  if (wp.deviceVariationsList && wp.deviceVariationsList.length > 0) {
    const deviceGroup = wp.deviceVariationsList.find(group => group.device === props.device)
    if (deviceGroup && deviceGroup.variations.length > 0) {
      return deviceGroup.variations.find(v => v.isPrimary) || deviceGroup.variations[0]
    }
  }

  const deviceVariation = wp.deviceVariations?.[props.device]
  if (deviceVariation) {
    return {
      imageUrl: deviceVariation.imageUrl,
      thumbnailUrl: deviceVariation.thumbnailUrl,
      isPrimary: true
    }
  }

  return null
}

const getSelectedPackLiveUrl = () => {
  const variation = getSelectedPackVariation()
  if (!variation?.imageUrl?.trim()) return ''
  return isLikelyVideoUrl(variation.imageUrl) ? variation.imageUrl : ''
}

const getStaticPreviewImageUrl = () => {
  const wp = wallpaper.value

  if (wp.isWallpaperPack) {
    const variation = getSelectedPackVariation()
    if (variation?.imageUrl?.trim()) {
      if (isLikelyVideoUrl(variation.imageUrl)) {
        if (variation.thumbnailUrl?.trim()) {
          return variation.thumbnailUrl
        }

        try {
          return cloudinaryService.generateThumbnailUrl(variation.imageUrl, props.targetWidth, props.targetHeight)
        } catch (error) {
          console.warn('[BaseDeviceWallpaperCard] Failed to generate poster frame for pack live variation:', error)
        }

        return ''
      }

      return variation.imageUrl || variation.thumbnailUrl || ''
    }

    if (variation?.thumbnailUrl?.trim()) {
      return variation.thumbnailUrl
    }
  }

  if (wp.isVideo || wp.isAnimated || wp.videoUrl || isLikelyVideoUrl(wp.imageUrl)) {
    if (wp.videoThumbnail?.trim()) {
      return wp.videoThumbnail
    }

    if (wp.thumbnailUrl?.trim()) {
      return wp.thumbnailUrl
    }

    if (wp.videoUrl?.trim()) {
      try {
        return cloudinaryService.generateThumbnailUrl(wp.videoUrl, props.targetWidth, props.targetHeight)
      } catch (error) {
        console.warn('[BaseDeviceWallpaperCard] Failed to generate poster frame for live wallpaper:', error)
      }
    }

    if (wp.imageUrl?.trim() && isLikelyVideoUrl(wp.imageUrl)) {
      try {
        return cloudinaryService.generateThumbnailUrl(wp.imageUrl, props.targetWidth, props.targetHeight)
      } catch (error) {
        console.warn('[BaseDeviceWallpaperCard] Failed to generate poster frame from imageUrl video:', error)
      }
    }

    return ''
  }

  return wp.imageUrl || wp.thumbnailUrl || ''
}

const staticPreviewUrl = computed(() => getStaticPreviewImageUrl())

const livePreviewUrl = computed(() => {
  const wp = wallpaper.value

  if (wp.videoUrl?.trim()) {
    return wp.videoUrl
  }

  const packLiveUrl = getSelectedPackLiveUrl()
  if (packLiveUrl) {
    return packLiveUrl
  }

  if (isLikelyVideoUrl(wp.imageUrl)) {
    return wp.imageUrl
  }

  return ''
})

const onLivePreviewLoad = () => {
  // Live preview is intentionally silent; the static preview remains visible until hover.
}

const onLivePreviewError = (event: Event) => {
  console.warn('[BaseDeviceWallpaperCard] Live preview failed to load:', event)
}
</script>
